import React from "react";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import { SonicTeamData } from "../../../../common/classes/SonicTeam";
import { SonicUserData } from "../../../../common/classes/SonicUser";
import Form from "../../../../common/forms/Form";
import FormInput from "../../../../common/forms/FormInput";
import Status from "../../../../common/management/detail/Status";
import ActionBtn from "../../../../common/management/ActionBtn";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";
import { ValType } from "../../../../utils/ValidationFunctions";
import {
  RequestState,
  genNotificationColor,
  genNotificationMessage,
} from "../../../../utils/RequestState";
import { TermType } from "../../../../utils/useTerm";
import styles from "./DatabaseDetail.module.scss";
import SelectMultipleTerms from "../../../../common/forms/SelectMultipleTerms";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import SonicLink from "../../../../common/routing/SonicLink";

type Props = {
  error: boolean;
  team: SonicTeamData;
  owner: SonicUserData;
  isLoading: boolean;
  selectStatus?: string;
  setSelectStatus: (value: string, name: string) => void;
  handleUpdate: () => void;
  handleInputChange: (value: File | string | undefined, name: string) => void;

  // MyGame
  games?: Array<SonicTermData>;
  handleGames: (value: Array<SonicTermData>) => void;

  showStatusMenu: boolean;
  handleStatusMenu: () => void;
  requestState: RequestState;
};

const TeamDetail: React.FC<Props> = ({
  error,
  team,
  owner,
  isLoading,
  selectStatus,
  setSelectStatus,
  handleUpdate,
  handleInputChange,
  games,
  handleGames,
  showStatusMenu,
  handleStatusMenu,
  requestState,
}) => {
  return (
    <div className={styles["f-databaseDetail"]}>
      {isLoading || !team || !owner ? (
        <LoadingSpinner space={1} />
      ) : (
        <>
          <h1 className={styles["f-databaseDetail__title"]}>チーム詳細</h1>
          <div className={styles["f-databaseDetail__inner"]}>
            <div className={styles["f-databaseDetail__head"]}>
              <Status
                isVisibilityStatus={true}
                status={team.status}
                selectStatus={selectStatus}
                showStatusMenu={showStatusMenu}
                handleStatusMenu={handleStatusMenu}
                setSelectStatus={setSelectStatus}
              />
              <div className={styles["f-databaseDetail__user"]}>
                <div className={styles["f-databaseDetail__user-head"]}>
                  <h2 className={styles["f-databaseDetail__user-head__title"]}>
                    ユーザー情報
                  </h2>
                  <SonicLink to={`/admin/member/${owner?.id}`}>
                    ユーザー詳細
                  </SonicLink>
                </div>
                <div className={styles["f-databaseDetail__user-info"]}>
                  <div>
                    <p>ユーザー名</p>
                    <span>{owner?.meta.name}</span>
                  </div>
                  <div>
                    <p>メールアドレス</p>
                    <span>{owner?.meta.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <Form handler={() => {}}>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  id="name"
                  type="text"
                  label="チーム名"
                  label_class={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                  name="sonic_name"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.name}
                  val_type={ValType.NotNull}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  id="kana"
                  type="text"
                  label="ふりがな"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_kana"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.kana}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  id="description"
                  type="textarea"
                  label="説明"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_description"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.description}
                  rows={3}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <div className={styles["f-databaseDetail__form-title"]}>
                  ゲーム
                </div>
                <div className={styles["f-databaseDetail__form-box"]}>
                  <div className={styles["mygame"]}>
                    <div className={styles["mygame-title"]}>
                      {"設定中のゲーム"}
                    </div>
                    <ul className={styles["mygame-list"]}>
                      {games && games.length > 0 ? (
                        games.map((game, i) => <li key={i}>{game.name}</li>)
                      ) : (
                        <li>{"まだゲームが設定されていないようです。"}</li>
                      )}
                    </ul>
                  </div>
                  <SelectMultipleTerms
                    termType={TermType.Games}
                    termName="ゲーム"
                    buttonText="ゲームを選ぶ"
                    terms={games ? games : []}
                    handleTerms={handleGames}
                    isAdminPage={true}
                  />
                </div>
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  id="twitter"
                  type="text"
                  label="X (旧Twitter)"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_twitter"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.sns.twitter?.toString()}
                  val_type={ValType.Twitter}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Instagram"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_instagram"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.sns.instagram?.toString()}
                  val_type={ValType.Instagram}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Facebook"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_facebook"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.sns.facebook?.toString()}
                  val_type={ValType.Facebook}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Youtube"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_youtube"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={
                    team.sns.youtube
                      ? team.sns.youtube.toString()
                      : "https://youtube.com/channel/"
                  }
                  val_type={ValType.Youtube}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="text"
                  label="Twitch"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_twitch"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={team.sns.twitch?.toString()}
                  val_type={ValType.Twitch}
                />
              </div>
            </Form>

            {error ? (
              <div className={styles["f-databaseDetail__error"]}>
                必須項目が未入力、または入力内容に誤りがあります
              </div>
            ) : null}

            <div className={styles["f-databaseDetail__bottom"]}>
              <ActionBtn
                label="保存"
                onClick={() => handleUpdate()}
                colorReversal={true}
              />
            </div>
            <div className={styles["f-databaseDetail__btn"]}>
              <ActionBtn
                label=" 保存 "
                onClick={() => handleUpdate()}
                colorReversal={true}
              />
            </div>
          </div>
        </>
      )}
      {requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor(requestState)}
          message={genNotificationMessage(requestState)}
        />
      ) : null}
    </div>
  );
};

export default TeamDetail;
